import type { ProjectTask } from "@/types/models";

export interface ProgressBreakdown {
  progress: number;
  included: number;
  finished: number;
  inProgress: number;
  earnedPoints: number;
}

export function calculateTaskProgress(
  tasks: readonly ProjectTask[],
): ProgressBreakdown {
  const included = tasks.filter(
    (task) => task.type !== "summary" && task.status !== "Cancelled",
  );
  const earnedPoints = Number(
    included.reduce((sum, task) => sum + task.progress / 100, 0).toFixed(4),
  );
  return {
    progress: included.length
      ? Math.round((earnedPoints / included.length) * 100)
      : 0,
    included: included.length,
    finished: included.filter((task) =>
      ["Complete", "Not applicable"].includes(task.status),
    ).length,
    inProgress: included.filter((task) => task.status === "In progress").length,
    earnedPoints,
  };
}

export function descendantTasks(
  tasks: readonly ProjectTask[],
  parentId: string,
): ProjectTask[] {
  const result: ProjectTask[] = [];
  const pending = [parentId];
  const visited = new Set<string>();
  while (pending.length) {
    const current = pending.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    for (const task of tasks) {
      if (task.parentId !== current) continue;
      result.push(task);
      if (task.type === "summary") pending.push(task.id);
    }
  }
  return result;
}

export function compareTasksBySchedule(
  left: ProjectTask,
  right: ProjectTask,
): number {
  const leftHasStart = Boolean(left.startDate);
  const rightHasStart = Boolean(right.startDate);
  if (leftHasStart !== rightHasStart) return leftHasStart ? -1 : 1;

  const startComparison = left.startDate.localeCompare(right.startDate);
  if (startComparison) return startComparison;

  const leftHasFinish = Boolean(left.dueDate);
  const rightHasFinish = Boolean(right.dueDate);
  if (leftHasFinish !== rightHasFinish) return leftHasFinish ? -1 : 1;

  return left.dueDate.localeCompare(right.dueDate);
}

export function visibleTaskHierarchy(
  visibleTasks: readonly ProjectTask[],
  allTasks: readonly ProjectTask[] = visibleTasks,
): ProjectTask[] {
  const allById = new Map(allTasks.map((task) => [task.id, task]));
  const includedIds = new Set(visibleTasks.map((task) => task.id));

  for (const task of visibleTasks) {
    const visited = new Set<string>();
    let parentId = task.parentId;
    while (parentId && !visited.has(parentId)) {
      visited.add(parentId);
      const parent = allById.get(parentId);
      if (!parent) break;
      includedIds.add(parent.id);
      parentId = parent.parentId;
    }
  }

  const included = allTasks.filter((task) => includedIds.has(task.id));
  const originalIndex = new Map(
    included.map((task, index) => [task.id, index]),
  );
  const compareSiblings = (left: ProjectTask, right: ProjectTask) =>
    compareTasksBySchedule(left, right) ||
    originalIndex.get(left.id)! - originalIndex.get(right.id)!;
  const children = new Map<string, ProjectTask[]>();
  const roots: ProjectTask[] = [];

  for (const task of included) {
    if (task.parentId && includedIds.has(task.parentId)) {
      children.set(task.parentId, [
        ...(children.get(task.parentId) || []),
        task,
      ]);
    } else {
      roots.push(task);
    }
  }

  roots.sort(compareSiblings);
  children.forEach((siblings) => siblings.sort(compareSiblings));

  const ordered: ProjectTask[] = [];
  const visited = new Set<string>();
  const append = (task: ProjectTask) => {
    if (visited.has(task.id)) return;
    visited.add(task.id);
    ordered.push(task);
    for (const child of children.get(task.id) || []) append(child);
  };
  roots.forEach(append);
  included.forEach(append);
  return ordered;
}
